import CheckIcon from '@mui/icons-material/Check';
import { Box, Button, Card, Container, Grid, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';

import { doc, getDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';

import { useSubscription } from 'AppRoutes/context/SubscriptionContext';
import invernos from 'assets/envelhecidas/4invernos.png';
import barrelCloseup from 'assets/envelhecidas/barrel-closeup.jpg';
import heroBarrel from 'assets/envelhecidas/hero_barrel_2.webp';
import SEOHead from 'components/SEOHead';
import { auth, firestore } from 'services/firebase/client';
import { seoConfig } from 'utils/seoConfig';
import { hasActiveSubscriptionInList } from 'utils/subscriptionStatus';
import type { ISubscription } from 'utils/types/Subscription';

import {
  BAR,
  displayFont,
  eyebrowSx,
  fullBleedSx,
  heroOverlaySx,
} from './styles';

type Bottle = {
  name: string;
  style: string;
  image: string;
  maturation: string;
  abv: string;
  volume: string;
  edition: string;
  bottles: string;
  note: string;
  price: string;
  whatsappMessage: string;
  available: boolean;
};

const bottles: Bottle[] = [
  {
    name: '4 invernos',
    style: 'Funky Tripel',
    image: invernos,
    maturation: '4 anos em barricas de carvalho de bourbon',
    abv: '9,5% ABV',
    volume: '355 ml',
    edition: 'Edição I',
    bottles: '40 garrafas',
    note: 'Algumas coisas, só o tempo pode transformar em arte.',
    price: 'R$ 40,00',
    whatsappMessage: 'Quero comprar a cerveja 4 Invernos',
    available: true,
  },
];

const BENEFITS = [
  'Envelhecimento em barris de bourbon, rum e vinho',
  'Edições numeradas e produzidas uma única vez',
  'Reserva exclusiva para membros do clube',
];

const CORMORANT_HREF =
  'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;1,400&display=swap';

const WHATSAPP_NUMBER = '5516997697037';

const isMobileDevice = () =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent,
  );

const getWhatsAppUrl = (message: string) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;

const openWhatsApp = (message: string) => {
  const url = getWhatsAppUrl(message);
  if (isMobileDevice()) {
    window.location.assign(url);
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};

const CheckMark = ({ onDark = false }: { onDark?: boolean }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 20,
      height: 20,
      borderRadius: '50%',
      bgcolor: onDark ? 'rgba(255,255,255,0.12)' : `${BAR.copper}26`,
      color: onDark ? BAR.copperBright : BAR.copper,
      flexShrink: 0,
    }}
  >
    <CheckIcon sx={{ fontSize: 12 }} />
  </Box>
);

const BottleCard = ({ bottle }: { bottle: Bottle }) => {
  const { hasActiveSubscription, loading, personalData } = useSubscription();
  const [showReserveMessage, setShowReserveMessage] = useState(false);
  const [canReserve, setCanReserve] = useState(false);
  const [checkingReserve, setCheckingReserve] = useState(true);

  const isLoggedIn = !loading && !!auth.currentUser;
  const whatsappUrl = getWhatsAppUrl(bottle.whatsappMessage);

  useEffect(() => {
    if (loading) return;

    if (hasActiveSubscriptionInList(personalData.subscriptions)) {
      setCanReserve(true);
      setCheckingReserve(false);
      return;
    }

    const user = auth.currentUser;
    if (!user) {
      setCanReserve(false);
      setCheckingReserve(false);
      return;
    }

    let cancelled = false;
    setCheckingReserve(true);

    getDoc(doc(firestore, 'subscription_users', user.uid))
      .then(userDoc => {
        if (cancelled) return;
        if (!userDoc.exists()) {
          setCanReserve(false);
          return;
        }
        const subscriptions = userDoc.data().subscriptions as
          | ISubscription[]
          | undefined;
        setCanReserve(hasActiveSubscriptionInList(subscriptions));
      })
      .catch(() => {
        if (!cancelled) setCanReserve(false);
      })
      .finally(() => {
        if (!cancelled) setCheckingReserve(false);
      });

    return () => {
      cancelled = true;
    };
  }, [loading, personalData.subscriptions, hasActiveSubscription]);

  const handleReserve = useCallback(() => {
    if (loading) return;

    if (canReserve) {
      openWhatsApp(bottle.whatsappMessage);
      return;
    }

    setShowReserveMessage(true);
  }, [bottle.whatsappMessage, canReserve, loading]);

  const reserveButtonSx = {
    mt: 3.5,
    py: 1.5,
    borderRadius: 0,
    bgcolor: BAR.copper,
    color: BAR.white,
    fontSize: '0.7rem',
    letterSpacing: '0.28em',
    textTransform: 'uppercase',
    fontWeight: 500,
    '&:hover': { bgcolor: BAR.copperBright },
  } as const;

  return (
    <Card
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        border: `1px solid ${BAR.border}`,
        borderRadius: 0,
        bgcolor: BAR.white,
        transition: 'border-color 0.3s, box-shadow 0.3s',
        '&:hover': {
          borderColor: `${BAR.copper}66`,
          boxShadow: '0 30px 60px -30px rgba(0,0,0,0.18)',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          aspectRatio: '3/4',
          bgcolor: BAR.white,
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={bottle.image}
          alt={bottle.name}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            objectPosition: 'center',
            transition: 'transform 0.7s',
            '.MuiCard-root:hover &': { transform: 'scale(1.03)' },
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            border: `1px solid ${BAR.copper}4d`,
            bgcolor: BAR.white,
            px: 1.25,
            py: 0.5,
            fontSize: '0.6rem',
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: BAR.copper,
          }}
        >
          {bottle.edition}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: 3.5 }}>
        <Typography
          component="h3"
          sx={{ fontFamily: displayFont, fontSize: '1.875rem', color: BAR.ink }}
        >
          {bottle.name}
        </Typography>
        <Typography
          sx={{
            mt: 0.5,
            fontSize: '0.72rem',
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: BAR.copper,
          }}
        >
          {bottle.style}
        </Typography>
        <Typography
          sx={{
            mt: 1.5,
            fontFamily: displayFont,
            fontSize: '1.75rem',
            lineHeight: 1,
            color: BAR.ink,
          }}
        >
          {bottle.price}
        </Typography>
        <Typography
          sx={{
            mt: 2.5,
            fontSize: '0.875rem',
            fontStyle: 'italic',
            lineHeight: 1.7,
            color: BAR.muted,
          }}
        >
          &ldquo;{bottle.note}&rdquo;
        </Typography>

        <Box
          component="dl"
          sx={{
            mt: 3,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px 8px',
            fontSize: '0.75rem',
            color: `${BAR.ink}bf`,
            m: 0,
          }}
        >
          {[
            ['Maturação', bottle.maturation],
            ['Teor', bottle.abv],
            ['Volume', bottle.volume],
            ['Edição', bottle.bottles],
          ].map(([label, value]) => (
            <React.Fragment key={label}>
              <Typography
                component="dt"
                sx={{
                  m: 0,
                  textTransform: 'uppercase',
                  letterSpacing: '0.2em',
                  color: `${BAR.ink}66`,
                }}
              >
                {label}
              </Typography>
              <Typography component="dd" sx={{ m: 0, textAlign: 'right' }}>
                {value}
              </Typography>
            </React.Fragment>
          ))}
        </Box>

        <Box sx={{ flex: 1 }} />

        {bottle.available ? (
          <>
            {canReserve && !loading && !checkingReserve ? (
              <Button
                component="a"
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                fullWidth
                sx={reserveButtonSx}
                onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                  if (!isMobileDevice()) return;
                  event.preventDefault();
                  openWhatsApp(bottle.whatsappMessage);
                }}
              >
                Reservar minha garrafa
              </Button>
            ) : (
              <Button
                onClick={handleReserve}
                disabled={loading || checkingReserve}
                fullWidth
                sx={reserveButtonSx}
              >
                Reservar minha garrafa
              </Button>
            )}
            {showReserveMessage && !canReserve && (
              <Typography
                sx={{
                  mt: 1.5,
                  fontSize: '0.75rem',
                  lineHeight: 1.6,
                  color: BAR.muted,
                }}
              >
                {isLoggedIn ? (
                  <>
                    Reserva apenas para assinantes com assinatura ativa.
                    Verifique o status em{' '}
                    <Box
                      component={Link}
                      to="/mysubscription"
                      sx={{
                        color: BAR.copper,
                        fontWeight: 500,
                        textDecoration: 'underline',
                      }}
                    >
                      Minhas Assinaturas
                    </Box>
                    .
                  </>
                ) : (
                  <>
                    Reserva apenas para assinantes. Caso seja um assinante,{' '}
                    <Box
                      component={Link}
                      to="/login"
                      sx={{
                        color: BAR.copper,
                        fontWeight: 500,
                        textDecoration: 'underline',
                      }}
                    >
                      faça o seu login
                    </Box>
                    .
                  </>
                )}
              </Typography>
            )}
          </>
        ) : (
          <Button
            disabled
            fullWidth
            sx={{
              mt: 3.5,
              py: 1.5,
              borderRadius: 0,
              border: `1px solid ${BAR.ink}26`,
              color: `${BAR.ink}66`,
              fontSize: '0.7rem',
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
            }}
          >
            Em breve
          </Button>
        )}
      </Box>
    </Card>
  );
};

const ComingSoon = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      minHeight: 320,
      border: `1px dashed ${BAR.copper}4d`,
      bgcolor: BAR.paperWarm,
      p: 5,
      textAlign: 'center',
    }}
  >
    <Typography sx={eyebrowSx}>Próximas edições</Typography>
    <Typography
      sx={{
        mt: 2.5,
        fontFamily: displayFont,
        fontSize: '1.5rem',
        lineHeight: 1.35,
        color: `${BAR.ink}cc`,
      }}
    >
      Outros barris estão descansando na adega.
    </Typography>
    <Typography
      sx={{
        mt: 2,
        maxWidth: 280,
        fontSize: '0.875rem',
        lineHeight: 1.7,
        color: BAR.muted,
      }}
    >
      Cada lançamento é anunciado apenas aos assinantes, alguns dias antes da
      abertura das reservas.
    </Typography>
  </Box>
);

const Envelhecidas = () => {
  useEffect(() => {
    const existing = document.querySelector(`link[href="${CORMORANT_HREF}"]`);
    if (existing) return undefined;

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = CORMORANT_HREF;
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  return (
    <Box sx={{ bgcolor: BAR.cream, color: BAR.ink }}>
      <SEOHead
        title="Envelhecidas — Clube da 42"
        description="A coleção de cervejas envelhecidas em barris do Clube da 42. Edições limitadas, exclusivas para assinantes."
        keywords="envelhecidas, cerveja envelhecida, barrel aged, clube da 42, edição limitada"
        url={`${seoConfig.site.url}/envelhecidas`}
      />

      {/* Hero — imagem full-bleed com texto sobreposto */}
      <Box
        component="section"
        sx={{
          ...fullBleedSx,
          mt: -4,
          position: 'relative',
          display: 'flex',
          alignItems: { xs: 'flex-end', md: 'center' },
          minHeight: {
            xs: 'min(92svh, 720px)',
            sm: 'min(88vh, 760px)',
            md: 640,
          },
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={heroBarrel}
          alt="Barris de carvalho — Coleção Envelhecidas"
          loading="eager"
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: {
              xs: 'center 35%',
              sm: 'center 40%',
              md: 'center center',
            },
          }}
        />
        <Box sx={heroOverlaySx} aria-hidden />
        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            px: { xs: 2.5, sm: 3 },
            pt: { xs: 10, md: 8 },
            pb: {
              xs: 'max(32px, env(safe-area-inset-bottom))',
              md: 8,
            },
          }}
        >
          <Box sx={{ maxWidth: { xs: '100%', md: 560, lg: 620 } }}>
            <Typography
              component="span"
              sx={{
                ...eyebrowSx,
                color: BAR.copperBright,
              }}
            >
              Coleção Envelhecidas
            </Typography>
            <Typography
              component="h1"
              sx={{
                mt: { xs: 2, md: 2.5 },
                fontFamily: displayFont,
                fontSize: { xs: '2.125rem', sm: '2.5rem', md: '3.75rem' },
                lineHeight: { xs: 1.1, md: 1.05 },
                fontWeight: 400,
                color: BAR.cream,
              }}
            >
              Cervejas que{' '}
              <Box
                component="em"
                sx={{ fontStyle: 'italic', color: BAR.copperBright }}
              >
                esperaram
              </Box>{' '}
              para serem bebidas.
            </Typography>
            <Typography
              sx={{
                mt: { xs: 2, md: 3 },
                maxWidth: 520,
                fontSize: { xs: '0.9375rem', md: '1.125rem' },
                lineHeight: 1.65,
                color: 'rgba(250, 249, 246, 0.88)',
              }}
            >
              Uma série limitada do Clube da 42 maturada em barris de carvalho.
              Lançamentos únicos, jamais repetidos — exclusivos para assinantes.
            </Typography>
            <Box
              component="ul"
              sx={{
                mt: { xs: 2.5, md: 3.5 },
                p: 0,
                listStyle: 'none',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {BENEFITS.map(benefit => (
                <Box
                  component="li"
                  key={benefit}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    mb: 1,
                    fontSize: { xs: '0.8125rem', md: '0.875rem' },
                    color: 'rgba(250, 249, 246, 0.9)',
                  }}
                >
                  <CheckMark onDark />
                  {benefit}
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                mt: { xs: 3, md: 4.5 },
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                flexWrap: 'wrap',
                gap: { xs: 1.5, sm: 2 },
              }}
            >
              <Button
                href="#colecao"
                fullWidth
                sx={{
                  px: 3.5,
                  py: { xs: 1.5, md: 1.75 },
                  borderRadius: 0,
                  bgcolor: BAR.copper,
                  color: BAR.white,
                  fontSize: { xs: '0.8125rem', md: '0.875rem' },
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': { bgcolor: BAR.copperBright },
                }}
              >
                Ver garrafas disponíveis
              </Button>
              <Button
                href="#sobre"
                fullWidth
                sx={{
                  px: 3.5,
                  py: { xs: 1.5, md: 1.75 },
                  borderRadius: 0,
                  border: '1px solid rgba(250, 249, 246, 0.45)',
                  color: BAR.cream,
                  fontSize: { xs: '0.8125rem', md: '0.875rem' },
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  fontWeight: 500,
                  width: { xs: '100%', sm: 'auto' },
                  '&:hover': {
                    borderColor: BAR.cream,
                    bgcolor: 'rgba(255, 255, 255, 0.08)',
                  },
                }}
              >
                Sobre a coleção
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* About */}
      <Box
        component="section"
        id="sobre"
        sx={{ bgcolor: BAR.paperWarm, py: { xs: 8, md: 12 } }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 5, md: 7 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  aspectRatio: '4/5',
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="img"
                  src={barrelCloseup}
                  alt="Barris de carvalho"
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography component="span" sx={eyebrowSx}>
                O que são as Envelhecidas
              </Typography>
              <Typography
                component="h2"
                sx={{
                  mt: 2.5,
                  fontFamily: displayFont,
                  fontSize: { xs: '2.25rem', md: '3rem' },
                  lineHeight: 1.15,
                  fontWeight: 400,
                }}
              >
                Cervejas que descansaram em carvalho até estarem prontas.
              </Typography>
              <Typography sx={{ mt: 3.5, lineHeight: 1.8, color: BAR.muted }}>
                Cada cerveja desta série é envelhecida em barris previamente
                usados para destilados — bourbon, rum, vinho. Meses, às vezes
                anos, em contato com a madeira até ganharem camadas que nenhuma
                receita alcança.
              </Typography>
              <Typography sx={{ mt: 2.5, lineHeight: 1.8, color: BAR.muted }}>
                São lotes pequenos. Produzidos uma única vez. Engarrafados,
                numerados e guardados até o ponto certo.
              </Typography>
              <Box
                sx={{
                  mt: 4,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    height: 1,
                    bgcolor: BAR.copper,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    letterSpacing: '0.28em',
                    textTransform: 'uppercase',
                    color: BAR.copper,
                  }}
                >
                  Exclusivo para assinantes do Clube da 42
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Collection */}
      <Box
        component="section"
        id="colecao"
        sx={{ bgcolor: BAR.cream, py: { xs: 8, md: 12 } }}
      >
        <Container maxWidth="lg">
          <Box sx={{ maxWidth: 560, mx: 'auto', textAlign: 'center' }}>
            <Typography component="span" sx={eyebrowSx}>
              À venda agora
            </Typography>
            <Typography
              component="h2"
              sx={{
                mt: 2.5,
                fontFamily: displayFont,
                fontSize: { xs: '2.25rem', md: '3rem' },
                fontWeight: 400,
              }}
            >
              A coleção
            </Typography>
            <Typography sx={{ mt: 2.5, color: BAR.muted }}>
              Edições numeradas, em quantidades pequenas. Quando acabam, não
              voltam mais.
            </Typography>
          </Box>

          <Grid container spacing={3} sx={{ mt: 6 }}>
            {bottles.map(bottle => (
              <Grid item xs={12} sm={6} lg={4} key={bottle.name}>
                <BottleCard bottle={bottle} />
              </Grid>
            ))}
            <Grid item xs={12} sm={6} lg={4}>
              <ComingSoon />
            </Grid>
          </Grid>

          <Typography
            id="adquirir"
            sx={{
              mt: 8,
              maxWidth: 480,
              mx: 'auto',
              textAlign: 'center',
              fontSize: '0.75rem',
              letterSpacing: '0.28em',
              textTransform: 'uppercase',
              color: BAR.muted,
            }}
          >
            A reserva é exclusiva para para assinantes do Clube da 42
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Envelhecidas;
